import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MonitoringService } from '@features/monitoring/services/monitoring.service';
import { LocalStorageService } from '@shared/services/utilities/local-storage.service';
import { NgSelectModule } from '@ng-select/ng-select';
import { DatePickerComponent } from '../../shared/components/date-picker/date-picker.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

type TimelineView = 'day' | 'week' | 'month';

interface SeismographCalendarSummaryDto {
  seismographId: number;
  serialNumber: string;
  days: SeismographCalendarDaySummaryDto[];
}

interface SeismographCalendarDaySummaryDto {
  date: string | Date;
  recordsCount: number;
  recordsExceededAlarmCount: number;
  recordsNotExceededAlarmCount: number;
}

interface TimelineDaySummary {
  key: string;
  recordsCount: number;
  recordsExceededAlarmCount: number;
  recordsNotExceededAlarmCount: number;
}

interface TimelineDay {
  date: Date;
  key: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  isWeekend: boolean;
}

interface TimelineDevice {
  deviceName: string;
  deviceId?: number;
  days: TimelineDaySummary[];
}

interface DeviceOption {
  id: number;
  serialNumber: string;
}

@Component({
  standalone: true,
  selector: 'app-timeline',
  templateUrl: './timeline.component.html',
  styleUrls: ['./timeline.component.scss'],
  imports: [CommonModule, FormsModule, AngularSvgIconModule, NgSelectModule, DatePickerComponent, TranslateModule]
})
export class TimelineComponent implements OnInit {
  currentSiteId?: number;
  devices: TimelineDevice[] = [];
  filteredDevices: TimelineDevice[] = [];
  deviceOptions: DeviceOption[] = [];
  days: TimelineDay[] = [];
  selectedStartDate = new Date();
  selectedView: TimelineView = 'week';
  selectedDeviceIds: number[] = [];
  isLoading = false;

  viewOptions: { label: string; value: TimelineView }[] = [];

  private readonly alertThreshold = 0;

  constructor(
    private monitoringService: MonitoringService,
    private localStorageService: LocalStorageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.currentSiteId = this.localStorageService.getItem('currentSiteID');
    this.initializeTranslations();
    this.translateService.onLangChange.subscribe(() => {
      this.initializeTranslations();
      this.days = this.buildDays(this.selectedStartDate);
    });
    this.loadDeviceOptions();
    this.refreshTimeline();
  }

  onViewChange(): void {
    this.refreshTimeline();
  }

  onStartDateChange(date: Date): void {
    this.selectedStartDate = date;
    this.refreshTimeline();
  }

  goToPreviousPeriod(): void {
    const current = new Date(this.selectedStartDate);
    this.selectedView === 'month'
      ? current.setMonth(current.getMonth() - 1)
      : current.setDate(current.getDate() - (this.selectedView === 'week' ? 7 : 1));
    this.selectedStartDate = current;
    this.refreshTimeline(current);
  }

  goToNextPeriod(): void {
    const current = new Date(this.selectedStartDate);
    this.selectedView === 'month'
      ? current.setMonth(current.getMonth() + 1)
      : current.setDate(current.getDate() + (this.selectedView === 'week' ? 7 : 1));
    this.selectedStartDate = current;
    this.refreshTimeline(current);
  }

  goToToday(): void {
    const today = new Date();
    this.selectedStartDate = today;
    this.refreshTimeline(today);
  }

  onDeviceChange(): void {
    this.refreshTimeline();
  }

  private updateFilteredDevices(): void {
    this.filteredDevices = [...this.devices];
  }

  getCount(device: TimelineDevice, day: TimelineDay): number {
    return this.getDaySummary(device, day)?.recordsCount ?? 0;
  }

  getAlertCount(device: TimelineDevice, day: TimelineDay): number {
    return this.getDaySummary(device, day)?.recordsExceededAlarmCount ?? 0;
  }

  getNormalCount(device: TimelineDevice, day: TimelineDay): number {
    return this.getDaySummary(device, day)?.recordsNotExceededAlarmCount ?? 0;
  }

  hasRecords(device: TimelineDevice, day: TimelineDay): boolean {
    return this.getCount(device, day) > 0;
  }

  getSelectedDeviceLabel(items: DeviceOption[]): string {
    const names = items.slice(0, 3).map(item => item.serialNumber).join(', ');
    return items.length > 3 ? `${names} ...` : names;
  }

  private getDaySummary(device: TimelineDevice, day: TimelineDay): TimelineDaySummary | undefined {
    return device.days.find(summary => summary.key === day.key);
  }

  private initializeTranslations(): void {
    this.viewOptions = [
      { label: this.translateService.instant('TIMELINE.VIEWS.DAY'), value: 'day' },
      { label: this.translateService.instant('TIMELINE.VIEWS.WEEK'), value: 'week' },
      { label: this.translateService.instant('TIMELINE.VIEWS.MONTH'), value: 'month' }
    ];
  }

  private refreshTimeline(anchorDate = this.selectedStartDate): void {
    this.days = this.buildDays(anchorDate);
    this.loadTimelineData();
  }

  private loadTimelineData(): void {
    if (!this.currentSiteId || this.days.length === 0) {
      return;
    }

    const startDate = new Date(this.days[0].date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(this.days[this.days.length - 1].date);
    endDate.setHours(23, 59, 59, 999);

    this.isLoading = true;
    this.monitoringService.getCalendarSummary({
      startDate: this.formatLocalDateTime(startDate),
      endDate: this.formatLocalDateTime(endDate),
      deviceId: this.selectedDeviceIds
    }).subscribe({
      next: response => {
        const data = this.extractCalendarSummary(response);
        this.devices = data.map(device => this.toTimelineDevice(device));
        this.updateFilteredDevices();
        this.isLoading = false;
      },
      error: () => {
        this.devices = [];
        this.filteredDevices = [];
        this.isLoading = false;
      }
    });
  }

  private loadDeviceOptions(): void {
    this.monitoringService.getMonitoringDeviceNames().subscribe({
      next: devices => {
        this.deviceOptions = (devices ?? []).map((device: any) => {
          if (typeof device === 'string') {
            return null;
          }

          return {
            id: device.id ?? device.deviceId ?? device.seismographId,
            serialNumber: device.serialNumber ?? device.deviceName ?? device.name
          };
        }).filter((device: DeviceOption | null): device is DeviceOption => device?.id != null && !!device.serialNumber);
      },
      error: () => {
        this.deviceOptions = [];
      }
    });
  }

  private extractCalendarSummary(response: any): SeismographCalendarSummaryDto[] {
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response?.data?.items)) {
      return response.data.items;
    }
    return [];
  }

  private toTimelineDevice(device: SeismographCalendarSummaryDto): TimelineDevice {
    return {
      deviceName: device.serialNumber,
      deviceId: device.seismographId,
      days: (device.days ?? [])
        .map(daySummary => this.toTimelineDaySummary(daySummary))
        .filter((daySummary): daySummary is TimelineDaySummary => daySummary !== null)
    };
  }

  private toTimelineDaySummary(daySummary: SeismographCalendarDaySummaryDto): TimelineDaySummary | null {
    const date = this.parseBackendLocalDate(daySummary.date);
    if (!date) {
      return null;
    }

    return {
      key: this.formatDateKey(date),
      recordsCount: daySummary.recordsCount,
      recordsExceededAlarmCount: daySummary.recordsExceededAlarmCount,
      recordsNotExceededAlarmCount: daySummary.recordsNotExceededAlarmCount
    };
  }

  private buildDays(anchorDate: Date): TimelineDay[] {
    const days: Date[] = [];
    const start = new Date(anchorDate.getFullYear(), anchorDate.getMonth(), anchorDate.getDate());
    const end = new Date(start);

    if (this.selectedView === 'month') {
      end.setMonth(end.getMonth() + 1);
      end.setDate(end.getDate() - 1);
    } else if (this.selectedView === 'week') {
      end.setDate(end.getDate() + 6);
    } else {
      end.setDate(end.getDate());
    }

    for (const current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
      days.push(new Date(current));
    }

    const todayKey = this.formatDateKey(new Date());
    return days.map(date => ({
      date,
      key: this.formatDateKey(date),
      dayName: this.translateService.instant(`DATE_PICKER.WEEKDAYS_SHORT.${this.getWeekDayKey(date)}`),
      dayNumber: date.getDate(),
      isToday: this.formatDateKey(date) === todayKey,
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    }));
  }

  private getWeekDayKey(date: Date): string {
    return ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][date.getDay()];
  }

  private formatDateKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private formatLocalDateTime(date: Date): string {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${this.formatDateKey(date)}T${hours}:${minutes}:${seconds}`;
  }

  private parseBackendLocalDate(value: any): Date | null {
    if (value == null) {
      return null;
    }
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }
    const dateOnlyMatch = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const date = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
      return isNaN(date.getTime()) ? null : date;
    }
    const match = String(value).trim().match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})(?:\.(\d{3}))?/);
    if (match) {
      const [, year, month, day, hours, minutes, seconds, ms] = match;
      const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds), Number(ms || '0'));
      return isNaN(date.getTime()) ? null : date;
    }
    return null;
  }
}
